<script setup lang="ts">
  defineOptions({
    name: 'ChatMessageEditorToolsPopover',
  })

  import { defineAsyncComponent, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { useGlobal } from '@common/core-layer/composables/useGlobal'

  import {
    FileAttachment04Icon,
    Film01Icon,
    Image01Icon,
    MusicNote01Icon,
    PlusCircleIcon,
    XCircleIcon,
  } from '@gohighlevel/ghl-icons/24/outline'
  import { MaterialGifBoxIcon } from '@gohighlevel/ghl-icons/24/material/rounded'

  import MediaPicker from '@common/media-layer/components/MediaPicker.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPPopover from '@ui/components/CPPopover.vue'
  import CPTooltip from '@ui/components/CPTooltip.vue'

  import type { ValidationError } from '@common/shared-ui-layer/types/validation.types'

  import type { ChatMessageEditorToolsValidationConfig } from '../../types/chat-message-editor.types'

  const FileUpload = defineAsyncComponent(() => import('@common/editor-layer/components/editor/tools/FileUpload.vue'))

  const GifPicker = defineAsyncComponent(() => import('@common/editor-layer/components/editor/tools/GifPicker.vue'))

  const VideoUploadModal = defineAsyncComponent(() => import('@common/editor-layer/components/editor/tools/VideoUploadModal.vue'))

  const props = defineProps({
    showToolsPopover: {
      type: Boolean,
      default: false,
    },
    showGifPicker: {
      type: Boolean,
      default: false,
    },
    showGifSheet: {
      type: Boolean,
      default: false,
    },
    showVideoUploadModal: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validations: {
      type: Object as PropType<ChatMessageEditorToolsValidationConfig>,
      default: undefined,
    },
    currentImageCount: {
      type: Number,
      default: 0,
    },
    currentVideoCount: {
      type: Number,
      default: 0,
    },
    currentFileCount: {
      type: Number,
      default: 0,
    },
  })

  const emit = defineEmits<{
    'update:showToolsPopover': [value: boolean]
    'update:showGifPicker': [value: boolean]
    'update:showGifSheet': [value: boolean]
    'update:showVideoUploadModal': [value: boolean]
    mediaSelected: [files: unknown[]]
    documentsSelected: [files: unknown[]]
    audioSelected: [files: unknown[]]
    gifSelected: [gif: { url: string; title: string }]
    videoUrlImported: [video: { url: string; type: string; platform: string; isImported: boolean }]
    validationError: [errors: ValidationError[]]
  }>()

  const { t } = useI18n()
  const { isMobileOrTablet } = useGlobal()

  const toggleToolsPopover = () => {
    emit('update:showToolsPopover', !props.showToolsPopover)
  }

  const handlePopoverHide = () => {
    emit('update:showGifPicker', false)
  }

  const toggleGifPicker = () => {
    if (isMobileOrTablet()) {
      emit('update:showToolsPopover', false)
      emit('update:showGifPicker', false)
      emit('update:showGifSheet', true)
      return
    }
    emit('update:showGifPicker', !props.showGifPicker)
  }
</script>

<template>
  <div class="chat-message-editor-tools">
    <CPTooltip placement="top" :disabled="showToolsPopover">
      <template #trigger>
        <span class="inline-flex">
          <CPPopover
            id="chat-message-editor-tools-popover"
            :show="showToolsPopover"
            trigger="click"
            placement="top-start"
            :show-arrow="false"
            :disabled="disabled"
            :to="false"
            @update:show="emit('update:showToolsPopover', $event)"
            @hide="handlePopoverHide"
          >
            <template #trigger>
              <CPButton
                id="chat-message-editor-attachments"
                type="text"
                size="sm"
                class="h-8 w-8 rounded-full !min-w-0 !p-0 text-body"
                :class="{ 'bg-hover': showToolsPopover }"
                :disabled="disabled"
                @click="toggleToolsPopover"
              >
                <XCircleIcon v-if="showToolsPopover" class="h-5 w-5" />
                <PlusCircleIcon v-else class="h-5 w-5" />
              </CPButton>
            </template>

            <div class="min-w-[10rem] rounded-lg border border-default bg-background py-1 shadow-lg">
              <div v-if="showGifPicker" class="gif-picker-container">
                <GifPicker variant="popover" @gifSelected="emit('gifSelected', $event)" />
              </div>
              <div v-else>
                <div class="cursor-pointer transition-colors hover:bg-hover">
                  <FileUpload
                    :validation="validations?.fileValidations"
                    :current-file-count="currentFileCount"
                    @filesSelected="emit('documentsSelected', $event)"
                    @validationError="emit('validationError', $event)"
                  >
                    <div class="flex items-center gap-2 px-3 py-2">
                      <FileAttachment04Icon class="h-4 w-4 text-body" />
                      <span class="text-sm font-medium text-body">
                        {{ t('chats.messageEditor.attachments.documents') }}
                      </span>
                    </div>
                  </FileUpload>
                </div>

                <div class="cursor-pointer transition-colors hover:bg-hover">
                  <FileUpload
                    id="chat-message-editor-tools-audio"
                    :multiple="false"
                    :validation="validations?.audioValidations"
                    @filesSelected="emit('audioSelected', $event)"
                    @validationError="emit('validationError', $event)"
                  >
                    <div class="flex items-center gap-2 px-3 py-2">
                      <MusicNote01Icon class="h-4 w-4 text-body" />
                      <span class="text-sm font-medium text-body">
                        {{ t('chats.messageEditor.attachments.audio') }}
                      </span>
                    </div>
                  </FileUpload>
                </div>

                <CPButton
                  id="chat-message-editor-tools-video"
                  type="text"
                  class="h-auto w-full !justify-start !rounded-none !p-0 !text-left transition-colors hover:bg-hover"
                  @click="emit('update:showVideoUploadModal', true)"
                >
                  <div class="flex items-center gap-2 px-3 py-2">
                    <Film01Icon class="h-4 w-4 text-body" />
                    <span class="text-sm font-medium text-body">
                      {{ t('chats.messageEditor.attachments.videos') }}
                    </span>
                  </div>
                </CPButton>

                <div class="cursor-pointer transition-colors hover:bg-hover">
                  <MediaPicker
                    variant="image"
                    :validation="{ imageValidations: validations?.mediaValidations?.imageValidations }"
                    :current-image-count="currentImageCount"
                    :current-video-count="0"
                    @filesSelected="emit('mediaSelected', $event)"
                    @validationError="emit('validationError', $event)"
                  >
                    <div class="flex items-center gap-2 px-3 py-2">
                      <Image01Icon class="h-4 w-4 text-body" />
                      <span class="text-sm font-medium text-body">
                        {{ t('chats.messageEditor.attachments.images') }}
                      </span>
                    </div>
                  </MediaPicker>
                </div>

                <CPButton
                  id="chat-message-editor-tools-gif"
                  type="text"
                  class="h-auto w-full !justify-start !rounded-none !p-0 !text-left transition-colors hover:bg-hover"
                  @click="toggleGifPicker"
                >
                  <div class="flex items-center gap-2 px-3 py-2">
                    <MaterialGifBoxIcon class="h-4 w-4 shrink-0 text-body" />
                    <span class="text-sm font-medium text-body">
                      {{ t('chats.messageEditor.attachments.gif') }}
                    </span>
                  </div>
                </CPButton>
              </div>
            </div>
          </CPPopover>
        </span>
      </template>
      <div class="max-w-[14rem] px-2 py-1 text-xs">
        {{ t('chats.messageEditor.tooltips.addMedia') }}
      </div>
    </CPTooltip>

    <VideoUploadModal
      :show="showVideoUploadModal"
      :validation="{ videoValidations: validations?.mediaValidations?.videoValidations }"
      :current-video-count="currentVideoCount"
      @update:show="emit('update:showVideoUploadModal', $event)"
      @filesSelected="emit('mediaSelected', $event)"
      @urlImported="emit('videoUrlImported', $event)"
      @validationError="emit('validationError', $event)"
    />
  </div>
</template>

<style scoped>
  .gif-picker-container {
    max-height: 400px;
    overflow: hidden;
  }
</style>
